import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Select, Tooltip } from 'common-src/components/base';
import { getRoleConfig } from 'common-src/features/auth';
import { apiRequest, getRestErrorMessage, HttpMethods } from 'common-src/features/rest';

import { ModalType } from 'src/components/base/ModalGroup';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';

import styles from './Reports.module.scss';

const TARGET_OPTIONS = [
  { label: 'Leads', value: 'leads' },
  { label: 'Closed Patients', value: 'patients' },
];

const JOB_TYPE = {
  CHECK: 'check',
  REPORT: 'report',
};

const JOB_CONFIG = {
  [JOB_TYPE.CHECK]: {
    endpointSuffix: 'eligibilityRequest',
    buttonText: 'Request Eligibility Check',
    successMessage: 'Eligibility check requested',
  },
  [JOB_TYPE.REPORT]: {
    endpointSuffix: 'eligibilityReport',
    buttonText: 'Request Eligibility Report',
    successMessage: 'Eligibility report requested',
  },
};

const BULK_DOCUMENTATION_FIELDS = [
  { header: 'Mrn', description: 'Patient MRN; sent as patient_id.' },
  { header: 'Date Start', description: 'Start date for the generated documentation range.' },
  { header: 'Date End', description: 'End date for the generated documentation range.' },
  { header: 'Chart Notes', description: 'Yes/No; includes chart notes.' },
  { header: 'Reading Logs Bgm', description: 'Yes/No; includes BGM reading logs.' },
  { header: 'Reading Logs Cgm', description: 'Yes/No; includes CGM reading logs.' },
  {
    header: 'Orders And Informed Consent',
    description: 'Yes/No; includes orders and informed consent documentation.',
  },
  {
    header: 'Rpm Claim Documentation',
    description: 'Yes/No; includes RPM claim documentation.',
  },
  { header: 'Em Claim Documentation', description: 'Yes/No; includes E/M claim documentation.' },
  {
    header: 'Hawb Claim Documentation',
    description: 'Yes/No; includes HAWB claim documentation.',
  },
  { header: 'Ccm Claim Documentation', description: 'Yes/No; includes CCM claim documentation.' },
  { header: 'Blood Pressure', description: 'Yes/No; includes blood pressure documentation.' },
  { header: 'Med Pickup Date', description: 'Yes/No; includes medication pickup date.' },
  { header: 'Hba1C', description: 'Yes/No; includes HbA1c documentation.' },
];

const BULK_DOCUMENTATION_TEMPLATE_URL =
  'https://docs.google.com/spreadsheets/d/1ZCX5fRwcD7JfMdaLWXvhH-3RMxXwa_IRkoZHD-TsFmo/view';

const Reports = () => {
  const [target, setTarget] = useState(TARGET_OPTIONS[0]);
  const [loadingType, setLoadingType] = useState(null);
  const { showAlert, AlertType } = useAlert();
  const dispatch = useDispatch();
  const permissions = useCustomSelector((state) => getRoleConfig(state)) || {};
  const actions = permissions.patientChart?.actions || {};
  const { canCreateBulkPortalAccess } = permissions;
  const handleSubmit = (type) => {
    const config = JOB_CONFIG[type];
    if (!config) return;

    setLoadingType(type);

    const endpoint = `jobRequests/${config.endpointSuffix}`;

    apiRequest({
      endpoint,
      method: HttpMethods.Post,
      body: { target: target.value },
    }).then(async (res) => {
      if (!res.ok) {
        const err = await res.json();
        const errMsg = getRestErrorMessage(err) || '';
        showAlert(AlertType.Error, '', errMsg);
        setLoadingType(null);
        return;
      }

      showAlert(AlertType.Success, '', config.successMessage);
      setLoadingType(null);
    });
  };

  return (
    <section className={styles.container}>
      {permissions.canRequestEligibilityReports && (
        <>
          <h2 className={styles.sectionTitle}>Eligibility Jobs</h2>
          <div className={styles.grid}>
            <p className={styles.note}>Pick a target for eligibility request</p>
            <Select
              id="eligibility-target"
              label="Target"
              options={TARGET_OPTIONS}
              onChange={(option) => setTarget(option)}
              value={target}
            />
            <div className={styles.buttonRow}>
              <Button
                id="eligibility-request"
                text="Request Eligibility Check"
                onClick={() => handleSubmit(JOB_TYPE.CHECK)}
                isLoading={loadingType === JOB_TYPE.CHECK}
              />
              <p className={styles.note}>
                Note: Run Request Eligibility Report at least 2 hours after running Request
                Eligibility Check
              </p>
              <Button
                id="eligibility-report"
                text="Request Eligibility Report"
                onClick={() => handleSubmit(JOB_TYPE.REPORT)}
                isLoading={loadingType === JOB_TYPE.REPORT}
              />
            </div>
          </div>
        </>
      )}

      {actions.canSendBulkDocumentation && (
        <>
          <div className={styles.bulkDocTitleRow}>
            <h2 className={styles.bulkDocTitle}>Bulk Documentation</h2>
            <Tooltip
              classNames={[styles.bulkDocTooltip]}
              title={
                <div className={styles.bulkDocTooltipContent}>
                  <p>
                    Expected CSV columns. Use MM/DD/YYYY for dates and Yes/No for document flags.
                  </p>
                  <ul>
                    {BULK_DOCUMENTATION_FIELDS.map(({ header, description }) => (
                      <li key={header}>
                        <strong>{header}</strong>: {description}
                      </li>
                    ))}
                  </ul>
                </div>
              }
            >
              <span className={styles.helpIcon}>?</span>
            </Tooltip>
          </div>
          <div className={styles.grid}>
            <p className={styles.note}>
              Generate documentation for multiple patients by uploading a CSV file
            </p>
            <a
              className={styles.templateLink}
              href={BULK_DOCUMENTATION_TEMPLATE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Google Sheet template
            </a>
            <div className={styles.buttonRow}>
              <Button
                id="bulk-documentation-button"
                text="Generate Bulk Documentation"
                onClick={() => dispatch(openModal(ModalType.SEND_BULK_DOCUMENTATION))}
              />
            </div>
          </div>
          {canCreateBulkPortalAccess && (
            <>
              <h2 className={styles.bulkPortalTitle}>Bulk Portal Access</h2>
              <div className={styles.grid}>
                <p className={styles.note}>
                  Create missing member portal access in bulk by choosing how many records to
                  process
                </p>
                <div className={styles.buttonRow}>
                  <Button
                    id="bulk-portal-access-button"
                    text="Create Bulk Portal Access"
                    onClick={() =>
                      dispatch(openModal(ModalType.CREATE_MISSING_MEMBER_PORTAL_ACCESS))
                    }
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default Reports;
